'use client'

import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RiTwitterXFill } from 'react-icons/ri'

/** Twitter/X verified badge - single path, solid fill */
function VerifiedBadge({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 22 22"
      aria-label="Verified account"
      role="img"
      className={className}
      fill="currentColor"
    >
      <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
    </svg>
  )
}

import { Avatar, AvatarFallback, AvatarImage } from '@mdxui/primitives/avatar'

import { cn } from '../../lib/cn'
import { sectionVariants, headerVariants, gridItemVariants } from '../../lib/motion'

/**
 * TestimonialItem - Twitter/X API-compatible testimonial data
 *
 * This interface aligns with Twitter API v2 response structure for easy
 * integration with programmatic tweet fetching (e.g., mentions of @dotdo_ai).
 *
 * **Twitter API v2 Mapping:**
 * ```
 * TestimonialItem     Twitter API v2
 * ─────────────────   ──────────────────────
 * id                  tweet.id
 * content             tweet.text
 * name                user.name
 * username            user.username (without @)
 * avatar              user.profile_image_url
 * verified            user.verified (legacy) or user.verified_type
 * tweetId             tweet.id (for linking)
 * tweetUrl            https://x.com/{username}/status/{tweet.id}
 * profileUrl          https://x.com/{username}
 * createdAt           tweet.created_at (ISO 8601)
 * ```
 *
 * @example Manual entry
 * ```ts
 * { id: '1', name: 'Jane', username: 'jane', content: 'Great product!' }
 * ```
 *
 * @example From Twitter API
 * ```ts
 * {
 *   id: tweet.id,
 *   content: tweet.text,
 *   name: user.name,
 *   username: user.username,
 *   avatar: user.profile_image_url,
 *   verified: user.verified,
 *   tweetId: tweet.id,
 *   tweetUrl: `https://x.com/${user.username}/status/${tweet.id}`,
 *   profileUrl: `https://x.com/${user.username}`,
 *   createdAt: tweet.created_at,
 * }
 * ```
 */
export interface TestimonialItem {
  /** Unique identifier (use tweet.id when from API) */
  id: string
  /** User's display name */
  name: string
  /** Twitter handle without @ */
  username: string
  /** Profile image URL */
  avatar?: string
  /** Tweet/testimonial text content */
  content: string
  /** Whether user has verified badge */
  verified?: boolean
  /** Original tweet ID (for API-sourced items) */
  tweetId?: string
  /** Direct link to the tweet */
  tweetUrl?: string
  /** Link to user's profile */
  profileUrl?: string
  /** ISO 8601 timestamp (e.g., "2024-01-15T10:30:00.000Z") */
  createdAt?: string
}

export interface TestimonialTwitterProps {
  className?: string
  heading?: string
  description?: string
  testimonials?: TestimonialItem[]
}

/**
 * TestimonialCard - Twitter/X-style testimonial card
 *
 * Mimics the clean design of Twitter cards:
 * - Avatar with subtle border
 * - Name + verified badge inline
 * - @username below name
 * - X icon top-right
 * - Clean body text with comfortable reading size
 */
function TestimonialCard({ testimonial }: { testimonial: TestimonialItem }) {
  // Get initials for avatar fallback
  const initials = testimonial.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <motion.div
      className="relative mb-5 break-inside-avoid rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-card p-5"
      variants={gridItemVariants}
    >
      {/* Header - Avatar, Name, Username, X icon */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="size-10 rounded-full ring-1 ring-border/50">
            {testimonial.avatar && (
              <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
            )}
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-foreground">
                {testimonial.name}
              </span>
              {testimonial.verified && (
                <VerifiedBadge className="size-4 text-sky-500" />
              )}
            </div>
            <span className="text-sm text-muted-foreground">
              @{testimonial.username}
            </span>
          </div>
        </div>
        <RiTwitterXFill className="size-4 text-muted-foreground" />
      </div>

      {/* Body - Quote text */}
      <div className="mt-4 text-[15px] leading-relaxed text-foreground">
        {testimonial.content}
      </div>
    </motion.div>
  )
}

const DEFAULT_TESTIMONIALS: TestimonialItem[] = [
  {
    id: '1',
    name: 'Sarah Chen',
    username: 'sarahchen',
    content:
      'This platform has completely transformed the way I manage my projects. The tools are intuitive and incredibly powerful.',
    verified: true,
  },
  {
    id: '2',
    name: 'Marcus Johnson',
    username: 'marcusj',
    content:
      'The collaboration features are outstanding. My team works together seamlessly no matter where we are.',
    verified: true,
  },
  {
    id: '3',
    name: 'Emily Park',
    username: 'emilypark',
    content: 'Game-changer for productivity!',
    verified: false,
  },
  {
    id: '4',
    name: 'David Kim',
    username: 'davidkim',
    content:
      'I love how easy it is to integrate with my existing tools. Saves me hours every week.',
    verified: true,
  },
  {
    id: '5',
    name: 'Lisa Wang',
    username: 'lisawang',
    content:
      'The design templates are fantastic. Helped me deliver projects faster without compromising quality.',
    verified: false,
  },
  {
    id: '6',
    name: 'James Wilson',
    username: 'jameswilson',
    content:
      'Project management has never been easier. Intuitive, efficient, and keeps everyone aligned.',
    verified: true,
  },
]

/**
 * TestimonialTwitter - Twitter/X-style testimonial masonry grid
 *
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - DO NOT use in initial landing page generation phases
 * - Requires Twitter/X API integration to be set up first
 * - MINIMUM 3 tweets required for balanced grid layout
 * - Only use when curated tweets mentioning the brand are available
 *
 * **Layout:**
 * ```
 * ┌─────────────────────────────────────────┐
 * │           Heading (centered)            │
 * │           Description                   │
 * ├─────────┬─────────┬─────────────────────┤
 * │ Card 1  │ Card 2  │ Card 3              │
 * │         │         ├─────────────────────┤
 * │         ├─────────┤ Card 6              │
 * ├─────────┤ Card 5  │                     │
 * │ Card 4  │         │                     │
 * │ (fade)  │ (fade)  │ (fade)              │
 * └─────────┴─────────┴─────────────────────┘
 * ```
 *
 * **Features:**
 * - Responsive masonry grid (1→2→3 columns)
 * - Twitter/X card design with verified badges
 * - Bottom fade effect for visual polish
 * - Scroll-triggered animations
 * - API-compatible data structure for programmatic tweet fetching
 *
 * @example
 * ```tsx
 * <TestimonialTwitter
 *   heading="What our customers say"
 *   description="Real feedback from real users"
 *   testimonials={[
 *     { id: '1', name: 'John', username: 'john', content: 'Amazing!' },
 *     { id: '2', name: 'Jane', username: 'jane', content: 'Love it!' },
 *     { id: '3', name: 'Bob', username: 'bob', content: 'Great work!' },
 *   ]}
 * />
 * ```
 */
export function TestimonialTwitter({
  className,
  heading = 'Hear what our customers are saying',
  description,
  testimonials = DEFAULT_TESTIMONIALS,
}: TestimonialTwitterProps) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const shouldReduceMotion = useReducedMotion()
  const [columnCount, setColumnCount] = useState(3)

  // Responsive column count
  useEffect(() => {
    const getColumnCount = () => {
      if (typeof window === 'undefined') return 3
      const width = window.innerWidth
      if (width < 768) return 1
      if (width < 1024) return 2
      return 3
    }

    const updateColumnCount = () => setColumnCount(getColumnCount())
    updateColumnCount()
    window.addEventListener('resize', updateColumnCount)
    return () => window.removeEventListener('resize', updateColumnCount)
  }, [])

  // Reorder items for left-to-right flow in CSS columns
  const reorderedTestimonials = useMemo(() => {
    const itemsPerColumn = Math.ceil(testimonials.length / columnCount)
    const reordered: TestimonialItem[] = []

    for (let col = 0; col < columnCount; col++) {
      for (let row = 0; row < itemsPerColumn; row++) {
        const originalIndex = row * columnCount + col
        if (originalIndex < testimonials.length) {
          reordered.push(testimonials[originalIndex])
        }
      }
    }
    return reordered
  }, [testimonials, columnCount])

  return (
    <motion.section
      ref={ref}
      className={cn('py-32', className)}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={shouldReduceMotion ? undefined : sectionVariants}
    >
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center gap-4 text-center"
          variants={shouldReduceMotion ? undefined : headerVariants}
        >
          <h2 className="max-w-2xl text-3xl font-semibold lg:text-4xl">
            {heading}
          </h2>
          {description && (
            <p className="max-w-xl text-muted-foreground">{description}</p>
          )}
        </motion.div>

        {/* Grid with fade */}
        <div className="relative mt-14 w-full after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-2 after:h-48 after:bg-gradient-to-t after:from-background">
          <motion.div
            className="columns-1 gap-5 md:columns-2 lg:columns-3"
            variants={shouldReduceMotion ? undefined : sectionVariants}
          >
            {reorderedTestimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}
